/**
 * VendorListLoader — Fetches and caches the IAB Global Vendor List.
 */

import type { VendorList } from "./TcfTypes";

const GVL_URL = "https://vendor-list.consensu.org/v3/vendor-list.json";
const CACHE_KEY = "pb_gvl";
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

export class VendorListLoader {
  private vendorList: VendorList | null = null;

  /** Returns cached vendor list if available, otherwise null */
  get(): VendorList | null {
    return this.vendorList;
  }

  /** Fetch vendor list with localStorage cache. Returns null on failure. */
  async load(): Promise<VendorList | null> {
    // Try localStorage cache first
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, expiresAt } = JSON.parse(cached) as { data: VendorList; expiresAt: number };
        if (Date.now() < expiresAt) {
          this.vendorList = data;
          return data;
        }
      }
    } catch {
      // Ignore storage errors
    }

    // Fetch from IAB CDN
    try {
      const res = await fetch(GVL_URL, {
        signal: AbortSignal.timeout(5000),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as VendorList;
      this.vendorList = data;

      // Cache in localStorage
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ data, expiresAt: Date.now() + CACHE_TTL })
        );
      } catch {
        // Ignore quota errors
      }

      return data;
    } catch {
      return null;
    }
  }
}
